'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { HelpCircle, Loader2, Trophy, RotateCcw, ArrowRight } from 'lucide-react';
import type { Language } from '@/lib/i18n';
import type { LevelTest } from '@/lib/lessonData';

interface Props {
  test: LevelTest;
  language: Language;
  onComplete: (score: number, passed: boolean) => void;
  previousResult?: { score: number; passed: boolean };
}

export default function LevelTestScreen({ test, language, onComplete, previousResult }: Props) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const handleSelect = (qId: string, optId: string) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [qId]: optId }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < test.questions.length) return;
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 800));

    let correct = 0;
    test.questions.forEach((q) => {
      const sel = q.options.find((o) => o.id === answers[q.id]);
      if (sel?.isCorrect) correct++;
    });
    const s = Math.round((correct / test.questions.length) * 100);
    const p = s >= test.passingScore;
    setScore(s);
    setPassed(p);
    setSubmitted(true);
    setIsSubmitting(false);
    setShowResult(true);
    onComplete(s, p);
  };

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
    setShowResult(false);
    setScore(0);
    setPassed(false);
  };

  const allAnswered = Object.keys(answers).length === test.questions.length;

  if (showResult) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-slide-up">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-700 text-foreground">{test.title[language]}</h1>
          <Link href="/learning-path-screen" className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary transition-colors">
            ✕
          </Link>
        </div>
        <div className="bg-card rounded-2xl border border-border p-8 text-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${passed ? 'bg-green-100' : 'bg-red-100'}`}>
            <Trophy size={36} className={passed ? 'text-green-600' : 'text-red-500'} />
          </div>
          <h2 className="text-2xl font-700 mb-2">
            {passed
              ? (language === 'fr' ? '🎉 Niveau complété!' : '🎉 Уровень завершён!')
              : (language === 'fr' ? '💪 Pas encore...' : '💪 Пока нет...')}
          </h2>
          <p className="text-4xl font-800 mb-2" style={{ color: passed ? '#10B981' : '#EF4444' }}>{score}%</p>
          <p className="text-sm text-muted-foreground mb-6">
            {language === 'fr' ? `Score minimum requis: ${test.passingScore}%` : `Минимальный балл: ${test.passingScore}%`}
          </p>
          {passed && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-sm font-600 text-green-800">
                {language === 'fr'
                  ? `✅ Niveau suivant débloqué! +${test.levelNumber * 100} XP bonus!`
                  : `✅ Следующий уровень разблокирован! +${test.levelNumber * 100} XP бонус!`}
              </p>
            </div>
          )}
          {!passed && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm font-600 text-red-800">
                {language === 'fr' ?'Révisez les leçons et réessayez. Votre progression est conservée.' :'Повторите уроки и попробуйте снова. Ваш прогресс сохранён.'}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {passed ? (
              <Link href="/learning-path-screen" className="flex items-center justify-center gap-2 w-full py-3 bg-primary text-white font-600 rounded-xl text-sm hover:bg-primary/90 transition-all active:scale-95">
                {language === 'fr' ? 'Continuer le parcours' : 'Продолжить путь'}
                <ArrowRight size={15} />
              </Link>
            ) : (
              <button onClick={handleRetry} className="flex items-center justify-center gap-2 w-full py-3 bg-primary text-white font-600 rounded-xl text-sm hover:bg-primary/90 transition-all active:scale-95">
                <RotateCcw size={14} />
                {language === 'fr' ? 'Réessayer le test' : 'Повторить тест'}
              </button>
            )}
            <Link href="/learning-path-screen" className="flex items-center justify-center gap-2 w-full py-3 border border-border text-muted-foreground font-600 rounded-xl text-sm hover:bg-secondary transition-all active:scale-95">
              {language === 'fr' ? 'Retour au parcours' : 'Вернуться к пути'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-slide-up">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-500">
            {language === 'fr' ? `Test de niveau — Score minimum: ${test.passingScore}%` : `Тест уровня — Минимальный балл: ${test.passingScore}%`}
          </p>
          <h1 className="text-lg font-700 text-foreground">{test.title[language]}</h1>
        </div>
        <Link href="/learning-path-screen" className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary transition-colors">
          ✕
        </Link>
      </div>

      {previousResult && (
        <div className={`p-3 rounded-xl border text-sm font-500 ${previousResult.passed ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
          {language === 'fr'
            ? `Dernier résultat: ${previousResult.score}% — ${previousResult.passed ? 'Réussi ✓' : 'Échoué — Réessayez'}`
            : `Последний результат: ${previousResult.score}% — ${previousResult.passed ? 'Сдан ✓' : 'Не сдан — Попробуйте снова'}`}
        </div>
      )}

      <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <HelpCircle size={20} className="text-info" />
          </div>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ? `${test.questions.length} questions — Répondez à toutes pour soumettre` : `${test.questions.length} вопросов — Ответьте на все для отправки`}
          </p>
        </div>

        {test.questions.map((question, qi) => (
          <div key={question.id} className="border border-border rounded-xl p-4">
            <p className="text-sm font-600 text-foreground mb-3">
              <span className="text-primary mr-2">{qi + 1}.</span>
              {question.question[language]}
            </p>
            <div className="space-y-2">
              {question.options.map((option) => {
                const isSelected = answers[question.id] === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => handleSelect(question.id, option.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-sm transition-all duration-150 active:scale-[0.99] ${
                      isSelected ? 'bg-primary/10 border-primary text-foreground' : 'bg-card border-border text-foreground hover:border-primary/40 hover:bg-secondary'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-primary bg-primary' : 'border-muted'}`}>
                      {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className="font-500">{option.text[language]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <button
          onClick={handleSubmit}
          disabled={!allAnswered || isSubmitting}
          className="w-full py-3 bg-primary text-white font-600 rounded-xl text-sm hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting && <Loader2 size={16} className="animate-spin" />}
          {isSubmitting
            ? (language === 'fr' ? 'Correction...' : 'Проверка...')
            : (language === 'fr' ? 'Soumettre le test' : 'Отправить тест')}
        </button>
      </div>
    </div>
  );
}
